import {noop} from 'lodash';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {computeCustomFieldIconName} from '../../../helpers/customFieldViewHelpers';
import {CustomAttributeModel, CustomFieldModel} from '../../../models/api/customFieldModel';
import {InlineEditingCustomAttribute} from '../../common/inlineEditing/inlineEditingCustomAttribute';
import {LensCustomFieldSearchDropdown} from './lensCustomFieldSearchDropdown';

interface LensCustomAttributePickerProps {
  customAttribute: CustomAttributeModel;
  availableCustomFields: ReadonlyArray<CustomFieldModel>;
  onUpdate: (arg: string) => void;
  onReplace: (model: CustomFieldModel) => void;
  onDelete: () => void;
  shouldStartInEditMode?: boolean;
}

const StyledContentDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

const StyledValueDiv = styled.div`
  margin: 0 10px;
  min-width: 150px;
  max-width: 250px;
  flex-grow: 1;
`;

export const LensCustomAttributePicker: FC<LensCustomAttributePickerProps> = ({
  customAttribute,
  availableCustomFields,
  onUpdate,
  onReplace,
  onDelete,
  shouldStartInEditMode,
}) => {
  const theme = useUserTheme();
  const {customField, value} = customAttribute;

  const searcheableCustomFields = useMemo(
    () => [customField, ...availableCustomFields],
    [availableCustomFields, customField],
  );

  return (
    <StyledContentDiv>
      <DropdownBoxCoordinator
        value={customField.name}
        leftIcon={computeCustomFieldIconName(customField.type, VisualSizesEnum.SMALL)}
        renderDropdown={(popoverProps) => (
          <LensCustomFieldSearchDropdown
            customFields={searcheableCustomFields}
            onSelect={onReplace}
            selectedIds={[customField.id]}
            {...popoverProps}
          />
        )}
        noMargin
        shouldDisablePadding
      />

      <StyledValueDiv>
        <InlineEditingCustomAttribute
          customAttribute={customAttribute}
          editedValue={value}
          onEdit={onUpdate}
          onEditSave={onUpdate}
          onEditCancel={noop}
          shouldStartInEditMode={shouldStartInEditMode}
        />
      </StyledValueDiv>
      <IconButton
        iconName="trashSmall"
        color={theme.greys.shade70}
        label={<Trans id="lens-multiselect-customfields-attribute-remove">Remove</Trans>}
        onClick={onDelete}
      />
    </StyledContentDiv>
  );
};
